import { fetchWithAuth } from "./http";

// ─── Types ───

export interface Scenario {
  scenario_id: string;
  project_id: string;
  scenario_name: string;
  description: string | null;
  config_snapshot: Record<string, any>;
  is_default: boolean;
  scenario_version: number;
  latest_run_status: string | null;
  latest_run_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  nameplate_capacity: number | null;
  poi_limit: number | null;
  tech_type_id: string | null;
  battery_flag: boolean | null;
  ppa_flag: boolean | null;
}

export interface ScenarioCreatePayload {
  scenario_name: string;
  description?: string;
  clone_from_scenario_id?: string;
  nameplate_capacity?: number;
  poi_limit?: number;
  tech_type_id?: string;
  cod_date?: string;
  battery_flag?: boolean;
  battery_capacity_mwh?: number;
  battery_duration_hours?: number;
  battery_charge_rate_mw?: number;
  battery_discharge_rate_mw?: number;
  battery_round_trip_eff?: number;
  battery_soc_min?: number;
  ppa_flag?: boolean;
  ppa_percentage?: number;
  ppa_capacity?: number;
}

export interface ScenarioPatchPayload {
  scenario_name?: string;
  description?: string;
  nameplate_capacity?: number;
  poi_limit?: number;
  tech_type_id?: string;
  cod_date?: string;
  battery_flag?: boolean;
  battery_capacity_mwh?: number;
  ppa_flag?: boolean;
  ppa_percentage?: number;
  ppa_capacity?: number;
}

export interface GenerationProfile {
  profile_id: string;
  project_id: string;
  scenario_id: string | null;
  profile_label: string;
  confidence_level: number | null;
  source: string | null;
  annual_generation_mwh: number | null;
  is_primary: boolean;
  created_at: string | null;
}

export interface ProfileCreatePayload {
  profile_label: string;
  confidence_level?: number;
  source?: string;
  scenario_id?: string;
  gen_hour_profile_mw: Array<{ timestamp: string; value: number }>;
  is_primary?: boolean;
}

export interface CategoryBreakdownEntry {
  category: string;
  value: number | null;
  usd: number | null;
}

export interface ProfileComparisonEntry {
  profile_label: string;
  profile_id: string | null;
  model_run_id: string | null;
  generation_mwh: number | null;
  dalys: number | null;
  pdfs: number | null;
  usd: number | null;
  co2e_kg: number | null;
  status: string | null;
  health_breakdown: CategoryBreakdownEntry[] | null;
  biodiversity_breakdown: CategoryBreakdownEntry[] | null;
  economic_breakdown: CategoryBreakdownEntry[] | null;
}

export interface ComparisonData {
  project_id: string;
  scenario_id: string | null;
  run_group_id: string | null;
  profiles: ProfileComparisonEntry[];
}

export interface RunGroupResponse {
  run_group_id: string;
  model_run_ids: string[];
  message: string;
}

// ─── Scenario CRUD ───

export const listScenarios = (projectId: string, token: string) =>
  fetchWithAuth<{ scenarios: Scenario[] }>(`/projects/${projectId}/scenarios`, token);

export const getScenario = (projectId: string, scenarioId: string, token: string) =>
  fetchWithAuth<Scenario>(`/projects/${projectId}/scenarios/${scenarioId}`, token);

export const createScenario = (projectId: string, payload: ScenarioCreatePayload, token: string) =>
  fetchWithAuth<Scenario>(`/projects/${projectId}/scenarios`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateScenario = (
  projectId: string,
  scenarioId: string,
  payload: ScenarioPatchPayload,
  token: string,
) =>
  fetchWithAuth<Scenario>(`/projects/${projectId}/scenarios/${scenarioId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const deleteScenario = (projectId: string, scenarioId: string, token: string) =>
  fetchWithAuth<{ message: string }>(`/projects/${projectId}/scenarios/${scenarioId}`, token, {
    method: "DELETE",
  });

export const cloneScenario = (
  projectId: string,
  scenarioId: string,
  newName: string,
  token: string,
) =>
  fetchWithAuth<Scenario>(
    `/projects/${projectId}/scenarios/${scenarioId}/clone?new_name=${encodeURIComponent(newName)}`,
    token,
    { method: "POST" },
  );

// ─── Profile CRUD ───

export const listProfiles = (projectId: string, token: string, scenarioId?: string) => {
  const params = scenarioId ? `?scenario_id=${scenarioId}` : "";
  return fetchWithAuth<{ profiles: GenerationProfile[] }>(
    `/projects/${projectId}/profiles${params}`,
    token,
  );
};

export const createProfile = (projectId: string, payload: ProfileCreatePayload, token: string) =>
  fetchWithAuth<GenerationProfile>(`/projects/${projectId}/profiles`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateProfile = (
  projectId: string,
  profileId: string,
  payload: { profile_label?: string; is_primary?: boolean },
  token: string,
) =>
  fetchWithAuth<GenerationProfile>(`/projects/${projectId}/profiles/${profileId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const deleteProfile = (projectId: string, profileId: string, token: string) =>
  fetchWithAuth<{ message: string }>(`/projects/${projectId}/profiles/${profileId}`, token, {
    method: "DELETE",
  });

// ─── Extended Queue ───

export const queueScenarioRun = (
  projectId: string,
  token: string,
  options?: {
    scenario_id?: string;
    profile_id?: string;
    run_all_profiles?: boolean;
    analysis_year?: number;
  },
) =>
  fetchWithAuth<{ message: string; model_run_id?: string } | RunGroupResponse>(
    `/projects/${projectId}/queue-scenario`,
    token,
    {
      method: "POST",
      body: JSON.stringify(options || {}),
    },
  );

// ─── Comparison ───

export const getComparison = (
  projectId: string,
  token: string,
  params: { run_group_id?: string; scenario_id?: string },
) => {
  const qs = new URLSearchParams();
  if (params.run_group_id) qs.set("run_group_id", params.run_group_id);
  if (params.scenario_id) qs.set("scenario_id", params.scenario_id);
  return fetchWithAuth<ComparisonData>(`/projects/${projectId}/compare?${qs}`, token);
};
